import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { PortalProcedureItem } from '@datazo/contracts';
import {
  clearPortalSession,
  fetchMyProcedures,
  portalDownloadUrl,
  readPortalPatient,
  readPortalToken,
} from './api';

/**
 * Listado de procedimientos del paciente con resultados disponibles. Cada
 * resultado ofrece "Ver" (vista previa en un modal aquí mismo) y "Descargar"
 * (guarda el archivo en el dispositivo). El endpoint exige `Authorization:
 * Bearer`, que no se puede inyectar en un `<a href>`, así que se obtiene el
 * archivo vía fetch + blob.
 */
export function PortalResultsPage(): JSX.Element {
  const navigate = useNavigate();
  const paciente = readPortalPatient();
  const [items, setItems] = useState<PortalProcedureItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [preview, setPreview] = useState<
    { url: string; tipo: string; nombre: string } | null
  >(null);

  useEffect(() => {
    if (readPortalToken() === null) return;
    let cancelled = false;
    fetchMyProcedures()
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error inesperado');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (readPortalToken() === null) {
    return <Navigate to="/portal/login" replace />;
  }

  const logout = (): void => {
    clearPortalSession();
    navigate('/portal/login');
  };

  // El endpoint exige `Authorization: Bearer`, que no se puede inyectar en un
  // `<a href>`; por eso descargamos vía fetch + blob y creamos un ObjectURL.
  const fetchBlobUrl = async (resultId: string): Promise<string | null> => {
    const url = portalDownloadUrl(resultId);
    const token = readPortalToken();
    if (url === null || token === null) {
      navigate('/portal/login');
      return null;
    }
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  // "Ver": vista previa en un modal aquí mismo (iframe para PDF, img para imagen).
  const verResult = async (resultId: string, tipo: string, nombre: string): Promise<void> => {
    try {
      const blobUrl = await fetchBlobUrl(resultId);
      if (blobUrl === null) return;
      setPreview({ url: blobUrl, tipo, nombre });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se pudo abrir el resultado');
    }
  };

  // "Descargar": guarda el archivo en el dispositivo del paciente.
  const descargarResult = async (resultId: string, filename: string): Promise<void> => {
    try {
      const blobUrl = await fetchBlobUrl(resultId);
      if (blobUrl === null) return;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se pudo descargar el resultado');
    }
  };

  const closePreview = (): void => {
    setPreview((p) => {
      if (p !== null) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  // Descarga desde el modal reutilizando el blob ya cargado en la vista previa.
  const descargarDesdePreview = (): void => {
    if (preview === null) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = preview.nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const total = useMemo(
    () => (items ?? []).reduce((acc, p) => acc + p.resultados.length, 0),
    [items],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-sky-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <img
            src="/logo-endomedical.png"
            alt="Endomedical"
            className="h-8 w-auto object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500">
              {paciente !== null
                ? `${paciente.nombres} ${paciente.apellidos} · CC ${paciente.documento}`
                : 'Portal del paciente'}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="text-sm text-slate-600 hover:text-teal-700"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-extrabold text-slate-900">Mis resultados</h1>
        <p className="text-sm text-slate-600 mt-1">
          Aquí encontrarás los informes y archivos de cada procedimiento que la clínica ya
          cargó.
        </p>

        {loading && (
          <div className="mt-6 text-slate-500 text-sm">Cargando tus resultados…</div>
        )}

        {error !== null && (
          <div className="mt-6 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
            {error}
          </div>
        )}

        {!loading && error === null && items !== null && items.length === 0 && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-600 text-sm">
            Aún no tienes resultados cargados. Cuando la clínica los suba, los verás aquí.
          </div>
        )}

        {items !== null && items.length > 0 && (
          <>
            <div className="mt-4 text-xs text-slate-500">
              {items.length} procedimiento{items.length === 1 ? '' : 's'} ·{' '}
              {total} archivo{total === 1 ? '' : 's'}
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {items.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                >
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      {formatDate(p.fecha)} · {p.hora}
                    </div>
                    <div className="text-base font-bold text-slate-900 mt-0.5">
                      {p.tipo}
                    </div>
                    {p.medicoNombre !== undefined && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        Médico tratante: {p.medicoNombre}
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex flex-col gap-2">
                    {p.resultados.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:border-teal-400 transition-colors"
                      >
                        <div
                          className={
                            r.tipo === 'pdf'
                              ? 'w-10 h-10 rounded-md grid place-items-center text-xs font-bold flex-shrink-0 bg-red-100 text-red-700'
                              : 'w-10 h-10 rounded-md grid place-items-center text-xs font-bold flex-shrink-0 bg-sky-100 text-sky-700'
                          }
                        >
                          {r.tipo === 'pdf' ? 'PDF' : 'IMG'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-900 truncate">
                            {r.nombreOriginal}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {(r.tamanoBytes / 1024).toFixed(0)} KB ·{' '}
                            {formatDateTime(r.subidoEn)}
                            {r.descripcion !== undefined && r.descripcion !== ''
                              ? ` · ${r.descripcion}`
                              : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => void verResult(r.id, r.tipo, r.nombreOriginal)}
                            className="px-3 py-1.5 rounded-md border border-teal-600 text-teal-700 text-xs font-semibold hover:bg-teal-50"
                          >
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => void descargarResult(r.id, r.nombreOriginal)}
                            className="px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700"
                          >
                            Descargar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="text-xs text-slate-400 text-center mt-10">
          © {new Date().getFullYear()} Endomedical IPS — Portal del paciente
        </div>
      </main>

      {/* Vista previa inline */}
      {preview !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex flex-col"
          onClick={closePreview}
        >
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-sm text-slate-900 truncate">
              {preview.nombre}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={descargarDesdePreview}
                className="px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700"
              >
                Descargar
              </button>
              <button
                type="button"
                onClick={closePreview}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100"
              >
                Cerrar
              </button>
            </div>
          </div>
          <div
            className="flex-1 min-h-0 overflow-auto bg-slate-800 grid place-items-center p-2"
            onClick={(e) => e.stopPropagation()}
          >
            {preview.tipo === 'pdf' ? (
              <iframe
                src={preview.url}
                title={preview.nombre}
                className="w-full h-full bg-white rounded"
              />
            ) : (
              <img
                src={preview.url}
                alt={preview.nombre}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  const meses = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${parseInt(d!, 10)} de ${meses[parseInt(m!, 10) - 1]} de ${y}`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
